import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Check, ChevronDown, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SearchableLabeledSelectProps {
  label: string;
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  error?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  labelClassName?: string;
  wrapperClassName?: string;
  required?: boolean;
  name?: string;
}

const SearchableLabeledSelect = React.forwardRef<HTMLButtonElement, SearchableLabeledSelectProps>(
  ({ 
    label, 
    options, 
    value, 
    onChange, 
    placeholder = "Select an option",
    searchPlaceholder = "Search...",
    error,
    disabled,
    icon,
    labelClassName,
    wrapperClassName,
    required = false,
    name,
  }, ref) => {
    const [open, setOpen] = useState(false);
    const id = `searchable-select-${Math.random().toString(36).substring(2, 9)}`;
    
    const selectedOption = options.find(option => option.value === value);
    
    return (
      <div className={cn("space-y-3", wrapperClassName)}>
        <Label 
          htmlFor={id} 
          className={cn(
            "text-base font-medium text-foreground", 
            error && "text-destructive",
            labelClassName
          )}
        >
          {icon && icon}
          {label}
          {required && <span className="text-destructive">*</span>}
        </Label>
        
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              ref={ref}
              id={id}
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              name={name}
              className={cn(
                "h-12 w-full justify-between text-base transition-all duration-200 focus-within:ring-2 focus-within:ring-primary/20",
                !selectedOption && "text-muted-foreground"
              )}
            >
              <div className="flex items-center gap-2">
                {icon && <MapPin className="h-4 w-4" />}
                {selectedOption ? selectedOption.label : placeholder}
              </div>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-0 z-[200] bg-background border-border shadow-lg">
            <Command>
              <CommandInput placeholder={searchPlaceholder} className="h-9" />
              <CommandEmpty>No option found.</CommandEmpty>
              <CommandList className="max-h-[200px] overflow-y-auto">
                <CommandGroup>
                  {options.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      onSelect={(currentValue) => {
                        onChange(currentValue === value ? "" : currentValue);
                        setOpen(false);
                      }}
                      className="hover:bg-muted focus:bg-muted"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === option.value ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {option.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        
        {error && (
          <p className="text-sm font-medium text-destructive flex items-center gap-1">
            <span className="text-xs">⚠</span>
            {error}
          </p>
        )}
      </div>
    );
  }
);

SearchableLabeledSelect.displayName = "SearchableLabeledSelect";

export { SearchableLabeledSelect };